// THE WALLETS MONEY SCREEN'S DECISIONS (UI rebuild; the operator's yes in the wallet session, 28 Oct 2026, sent on by the Command Centre) —
// pure, so tests/custody-money pins them where they are decided.
//
// Four blocks: one status line · Needs you (only when not empty) · where the money sits, one row per role · today's moves.
// What the reads do not carry is said as not known, never a figure made up (the wallet's answers, 28 Oct 2026):
//   - the Hot day cap is /api/custody/limits dayCeilingUsdt (with usedTodayUsdt); a 503 limits_unset = not known. 150,000 was a doc example.
//   - the fee wallet's activations left: summary.feeFloat (the wallet's P-072, db/0110, 3 Nov 2026) — its count and condition; null
//     (before db/0110, or the wallet server missed two checks) is not known, beside the fee TRX (summary.feeTrx).
//   - the next sweep: sweeps are not scheduled — summary.window {open, expiresAt} says whether sweeps run now and until when.
//   - TRON reads: balances.freshness says what the Mac read, and when; summary.tron.stalled (P-072) says the chain's head has not
//     moved for 30 minutes on any provider — the bot's "TRON reads stalled" — and since when.
import type { ApprovalRow, CustodySummary, DepositRow, Freshness, MoveLimits, WalletBalance } from '../../../lib/api';
import { CLASS_WORD } from '../../../lib/custody-format';
import { poolTotal } from '../../../lib/custody-screen';
import { fmtAmount, fmtMoney } from '../../../lib/format';

/** The roles, in the page's order: where the money rests, where it pays from, where it waits, what feeds it. */
export const ROLE_ORDER = ['treasury', 'running_hot', 'dirty_hot', 'frozen', 'deposit', 'fee'] as const;
export type Role = typeof ROLE_ORDER[number];

/** A role's words: the register's group word (N-091) — Treasury, Hot, Merchant Hot, Flagged, Deposit, Fee. */
export function roleWord(r: Role): string {
  return CLASS_WORD[r] ?? r;
}

export interface RoleRow {
  role: Role;
  word: string;
  wallets: WalletBalance[];
  /** One total per asset the role holds; `total` null when no wallet of it was read (never 0). */
  totals: Array<{ asset: string; total: string | null; read: number; all: number }>;
  /** The role's own line: the Hot day cap, the deposit addresses' count, the fee TRX. */
  note: string | null;
}

/** The deposits that wait for a sweep: routed, and their sweep not done, failed or expired. */
export const toSweep = (d: Pick<DepositRow, 'status' | 'sweep'>): boolean =>
  d.status === 'routed' && !['swept', 'failed', 'expired'].includes(d.sweep?.status ?? '');

/** One row per role that has a wallet, in ROLE_ORDER. */
export function roleRows(i: { balances: readonly WalletBalance[]; deposits: readonly DepositRow[] | null; limits: MoveLimits | null; summary: CustodySummary | null }): RoleRow[] {
  const out: RoleRow[] = [];
  for (const role of ROLE_ORDER) {
    const wallets = i.balances.filter((w) => w.class === role);
    if (wallets.length === 0) continue;
    const assets = [...new Set(wallets.map((w) => w.asset))];
    const totals = assets.map((asset) => ({ asset, ...poolTotal(wallets.filter((w) => w.asset === asset)) }));   // dialect-ok: per chain — USDT on TRON and on Ethereum are two holdings
    out.push({ role, word: roleWord(role), wallets, totals, note: roleNote(role, wallets, i) });
  }
  return out;
}

function roleNote(role: Role, wallets: readonly WalletBalance[], i: { deposits: readonly DepositRow[] | null; limits: MoveLimits | null; summary: CustodySummary | null }): string | null {
  if (role === 'running_hot') {
    if (!i.limits) return 'Day cap not known';
    return `${fmtAmount(i.limits.usedTodayUsdt, 'USDT')} of ${fmtAmount(i.limits.dayCeilingUsdt, 'USDT')} USDT day cap used`;
  }
  if (role === 'deposit') {
    const sweep = i.deposits ? i.deposits.filter(toSweep).length : null;
    return `${wallets.length} open${sweep === null ? '' : ` · ${sweep} to sweep`}`;
  }
  if (role === 'fee') {
    const f = i.summary?.feeFloat;
    if (f) {
      if (f.activationsLeft !== null) {
        const n = `${f.activationsLeft} ${f.activationsLeft === 1 ? 'activation' : 'activations'} left`;
        return f.condition === 'low' ? `${n} · low` : f.condition === 'empty' ? `${n} · empty` : f.condition === 'over_ceiling' ? `${n} · over its ceiling` : n;
      }
      if (f.condition === 'unreadable') return 'Fee not read';
      if (f.condition === 'unpriced') return 'Activations not priced';
    }
    return i.summary?.feeTrx != null ? 'Activations left not known' : 'Fee TRX and activations left not known';
  }
  return null;
}

export interface NeedLine { key: 'sign' | 'held' | 'flagged'; words: string; action: string; to: string }

/** NEEDS YOU: payouts to sign, deposits held, money in Flagged — one line and one button each; empty when nothing waits. A read that failed or
 *  has not answered adds no line (the screen says the read failed; it never claims "nothing waits" from it). */
export function needLines(i: { approvals: readonly ApprovalRow[] | null; deposits: readonly DepositRow[] | null; balances: readonly WalletBalance[] | null }): NeedLine[] {
  const out: NeedLine[] = [];
  const payouts = (i.approvals ?? []).filter((a) => a.kind === 'payout');
  if (payouts.length > 0) {
    out.push({ key: 'sign', words: `${payouts.length} payout${payouts.length === 1 ? '' : 's'} to sign`, action: 'Sign', to: '/custody/approvals' });
  }
  const held = (i.deposits ?? []).filter((d) => d.status === 'held');
  if (held.length > 0) {
    const untagged = held.filter((d) => !d.partySlug).length;
    out.push({
      key: 'held',
      words: `${held.length} deposit${held.length === 1 ? '' : 's'} held${untagged ? ` · ${untagged} not tagged to a customer` : ''}`,
      action: untagged ? 'Tag customer' : 'Open', to: '/custody/deposits',
    });
  }
  const flagged = (i.balances ?? []).filter((w) => w.class === 'frozen' && w.balance !== null && Number(w.balance) > 0);
  if (flagged.length > 0) {
    const assets = [...new Set(flagged.map((w) => w.asset))];
    const said = assets.map((a) => { const t = poolTotal(flagged.filter((w) => w.asset === a)).total;   // dialect-ok: per chain, as above
      return t ? fmtMoney(t, a) : null; }).filter(Boolean).join(' · ');
    out.push({ key: 'flagged', words: `${said} held in Flagged`, action: 'Open', to: '/custody/balances?role=frozen' });
  }
  return out;
}

/** THE STATUS LINE: what the Mac last read on TRON, the fee TRX, and whether sweeps run now. Each part says only what its read says. */
export function statusParts(i: { freshness: Freshness | null; asOf: string | null; summary: CustodySummary | null; now: number; relative: (iso: string, now: number) => string; time: (iso: string) => string }): string[] {
  // A stalled head first: the reads may be fresh while the chain itself stands still (P-072).
  const stalled = i.summary?.tron?.stalled === true;
  const read = stalled ? (i.summary!.tron!.since ? `TRON stalled since ${i.time(i.summary!.tron!.since)}` : 'TRON stalled')
    : i.freshness === null ? 'TRON read not known'
    : i.freshness === 'blind' || !i.asOf ? 'TRON not read'
    : i.freshness === 'stale' ? `TRON last read ${i.relative(i.asOf, i.now)}` : `TRON read ${i.relative(i.asOf, i.now)}`;
  const fee = i.summary?.feeTrx != null ? `Fee ${fmtMoney(i.summary.feeTrx, 'TRX')}` : 'Fee not known';
  const w = i.summary?.window;
  const sweeps = !w ? 'Sweeps not known' : w.open ? (w.expiresAt ? `Sweeps running until ${i.time(w.expiresAt)}` : 'Sweeps running') : 'Sweeps not running';
  return [read, fee, sweeps];
}

